import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes } from 'react';
import { Link as RouterLink, type LinkProps as RouterLinkProps } from 'react-router-dom';

type ButtonType = 'solid-fill' | 'outline' | 'text' | 'icon-only' | 'outline-subtle';
type ButtonSize = 'sm' | 'md';
type ButtonState = 'default' | 'alert';
type IconPosition = 'start' | 'end';

interface BaseButtonProps {
  /** ボタンのタイプ */
  buttonType?: ButtonType;
  /** ボタンのサイズ */
  size?: ButtonSize;
  /** ボタンの状態（アラートなど） */
  state?: ButtonState;
  /** アイコンの位置 */
  iconPosition?: IconPosition;
  /** アクティブ状態（icon-only用） */
  active?: boolean;
  /** ボタンの内容 */
  children?: React.ReactNode;
  /** アイコン */
  icon?: string;
  /** 追加のクラス名 */
  className?: string;
}

type ButtonAsButton = BaseButtonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> & {
    as?: 'button';
    href?: never;
    to?: never;
  };

type ButtonAsAnchor = BaseButtonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps> & {
    as: 'a';
    href: string;
    to?: never;
  };

type ButtonAsRouterLink = BaseButtonProps &
  Omit<RouterLinkProps, keyof BaseButtonProps | 'className'> & {
    as: 'link';
    to: string;
    href?: never;
  };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsRouterLink;

/**
 * AIPデザインシステムのボタンコンポーネント
 */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      buttonType = 'solid-fill',
      size = 'md',
      state = 'default',
      iconPosition,
      active,
      children,
      icon,
      className = '',
      as = 'button',
      ...props
    },
    ref
  ) => {
    const dataAttributes: Record<string, string | undefined> = {
      'data-type': buttonType,
      'data-size': size,
    };

    if (state !== 'default') {
      dataAttributes['data-state'] = state;
    }

    if (iconPosition) {
      dataAttributes['data-icon-position'] = iconPosition;
    }

    if (active !== undefined) {
      dataAttributes['data-active'] = String(active);
    }

    const iconElement = icon ? (
      <span className="material-symbols-outlined">{icon}</span>
    ) : null;

    const content = (
      <>
        {iconPosition === 'start' && iconElement}
        {children}
        {iconPosition === 'end' && iconElement}
        {!iconPosition && iconElement}
      </>
    );

    const combinedClassName = `aip-button ${className}`.trim();

    if (as === 'a') {
      const { href, ...anchorProps } = props as ButtonAsAnchor;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={combinedClassName}
          {...dataAttributes}
          {...anchorProps}
        >
          {content}
        </a>
      );
    }

    if (as === 'link') {
      const { to, ...linkProps } = props as ButtonAsRouterLink;
      return (
        <RouterLink
          to={to}
          className={combinedClassName}
          {...dataAttributes}
          {...linkProps}
        >
          {content}
        </RouterLink>
      );
    }

    const buttonProps = props as ButtonAsButton;
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={buttonProps.type || 'button'}
        className={combinedClassName}
        {...dataAttributes}
        {...buttonProps}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;

